import React, { useState, useEffect } from 'react';
import * as api from '@/api/client';
import {
  Settings, Key, Save, Loader2, CheckCircle,
  AlertTriangle, ArrowLeft, Activity, Sliders, ChevronDown, ChevronRight
} from 'lucide-react';
import SystemConfigPanel from '@/components/settings/SystemConfigPanel';
import ModelConfigTable from '@/components/admin/ModelConfigTable';
import AppFooter from '@/components/layout/AppFooter';

// Provider tiles and their companion metadata. `apiKeyField` is the settings
// key we POST; `docs` is a one-line hint the UI shows next to the API key
// input so drafters know where to get it.
const PROVIDERS = [
  // No provider-prefix hints in the placeholder — "sk-...", "AIza..."
  // etc. looked superficially like leaked key content and drafters
  // asked for a cleaner empty state. A blank input now prompts
  // "Enter API key"; a stored key shows the neutral "Key stored —
  // type to replace" sentinel driven by the empty-but-present flag.
  { value: 'openai',     label: 'OpenAI',     apiKeyField: 'openai_api_key',     placeholder: 'Enter API key', docs: 'platform.openai.com/api-keys' },
  { value: 'anthropic',  label: 'Anthropic',  apiKeyField: 'anthropic_api_key',  placeholder: 'Enter API key', docs: 'console.anthropic.com' },
  { value: 'google',     label: 'Google',     apiKeyField: 'google_api_key',     placeholder: 'Enter API key', docs: 'aistudio.google.com/apikey' },
  { value: 'openrouter', label: 'OpenRouter', apiKeyField: 'openrouter_api_key', placeholder: 'Enter API key', docs: 'openrouter.ai/keys' },
  { value: 'fireworks',  label: 'Fireworks',  apiKeyField: 'fireworks_api_key',  placeholder: 'Enter API key', docs: 'fireworks.ai/account/api-keys' },
];

// Provider-specific model suggestion chips were removed — ModelConfigTable
// now owns the Models surface with a free-text model input (no hardcoded
// model names that can go stale). The PROVIDERS array above still drives
// the API Keys section; everything else routes through ModelConfigTable.


/**
 * ApiKeyStatusChips — one-row summary of which provider keys are
 * available in the environment or DB. Sits between the API Keys card
 * (where keys are entered) and ModelConfigTable (where they're
 * consumed) so admins see at a glance which direct-API routes are
 * actually usable.
 *
 * Reads ``GET /admin/config/api-keys/status`` once on mount. Silent
 * on fetch failure so a misconfigured endpoint can't break the
 * Settings page. The existing green/amber chip vocabulary matches
 * the API Keys Status indicator already used inside
 * SystemConfigPanel's Models tab.
 */
function ApiKeyStatusChips() {
  const [status, setStatus] = useState<api.ApiKeyStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.getApiKeyStatus();
        if (!cancelled) setStatus(s);
      } catch {
        if (!cancelled) setStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!status) return null;
  const chips: Array<{ label: string; ok: boolean; source: 'db' | 'env' | null }> = [
    { label: 'Anthropic',  ok: !!status.anthropic?.configured,  source: status.anthropic?.source  ?? null },
    { label: 'OpenAI',     ok: !!status.openai?.configured,     source: status.openai?.source     ?? null },
    { label: 'Google',     ok: !!status.google?.configured,     source: status.google?.source     ?? null },
    { label: 'OpenRouter', ok: !!status.openrouter?.configured, source: status.openrouter?.source ?? null },
    { label: 'Fireworks',  ok: !!status.fireworks?.configured,  source: status.fireworks?.source  ?? null },
  ];
  return (
    <div
      data-testid="api-keys-status"
      className="flex items-center gap-2 flex-wrap text-[11px]"
    >
      <span className="font-mono uppercase tracking-widest text-zinc-500">API Keys:</span>
      {chips.map((c) => (
        <span
          key={c.label}
          data-testid={`api-key-chip-${c.label.toLowerCase()}`}
          title={
            c.ok
              ? `${c.label} API key is set (source: ${c.source || 'unknown'})`
              : `${c.label} API key is not set — add it above or set ${c.label.toUpperCase()}_API_KEY in the environment`
          }
          className={
            'inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium '
            + (c.ok
              ? 'bg-green-50 text-green-800 border-green-200'
              : 'bg-amber-50 text-amber-800 border-amber-200')
          }
        >
          {c.ok ? '✅' : '⚠'} {c.label}
          {c.ok && c.source && (
            <span className="text-[9px] font-mono text-green-700/80 uppercase tracking-wider">
              ({c.source})
            </span>
          )}
        </span>
      ))}
    </div>
  );
}


export default function SettingsPage({ onBack }: { onBack: () => void }) {
  const [settings, setSettings] = useState<Record<string, string>>({});
  // Track which api_key fields the drafter has actually typed in during this
  // session. The backend returns keys in masked form ("sk-abc…wxyz") so we
  // must avoid posting that mask back. Only keys present in this set are sent
  // when saving; everything else is left untouched on the server.
  const [editedKeys, setEditedKeys] = useState<Set<string>>(new Set());
  // Map of settings-key → env var name, for the keys the server is currently
  // sourcing from environment variables (e.g. { openai_api_key: "OPENAI_API_KEY" }).
  // Used to badge inputs so drafters can see that a "stored" key is
  // actually coming from an env var — which is persistent across
  // redeploys, unlike the DB (now gitignored).
  const [envSources, setEnvSources] = useState<Record<string, string>>({});
  // Runtime-resolved model ids — the same shape /settings/active-models
  // returns. Pulled in parallel with /settings so the "Active
  // configuration" summary shows what the LLM ACTUALLY uses at
  // dispatch time, not the stale legacy settings row. Before this
  // the summary read from the legacy settings table while the
  // ⚙ → Active Models dropdown read from system_config, so the two
  // UIs disagreed (Settings said "gpt-4.1", dropdown said
  // "claude-sonnet-4-6").
  const [activeModels, setActiveModels] = useState<null | {
    fast_model?: string; draft_model?: string; tree_model?: string;
    vision_model?: string; qc_model?: string; provenance_model?: string;
    pageindex_enabled?: boolean;
  }>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<null | { status: string; provider?: string; model_fast?: string; model_capable?: string; latency_ms?: number; error?: string | null }>(null);
  const [checking, setChecking] = useState(false);

  const runHealthCheck = async () => {
    setChecking(true);
    try {
      const res = await api.getLlmHealth();
      setHealth(res);
    } catch (e: any) {
      setHealth({ status: 'error', error: e?.message || 'health check failed' });
    }
    setChecking(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const settingsData = await api.getSettings();
      setSettings(settingsData.settings || {});
      setEnvSources(settingsData.env_sources || {});
      // Reset dirty tracking — the newly loaded values are, by definition,
      // not edits.
      setEditedKeys(new Set());
      // Fetch the runtime-resolved model ids so the summary below
      // shows what callLLM will actually dispatch. Best-effort — if
      // this fails the summary falls back to the legacy settings row.
      try {
        const am = await api.getActiveModels();
        setActiveModels(am || null);
      } catch {
        setActiveModels(null);
      }
    } catch (e: any) {
      setError('Failed to load settings');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      // Strip untouched (still-masked) api_key fields so we never overwrite
      // the stored secret with its mask. See backend `update_settings` which
      // double-checks this server-side. `Object.entries` returns [string,
      // unknown][] under strict TS even though `settings` is typed as
      // Record<string,string> — coerce to string explicitly.
      const payload: Record<string, string> = {};
      for (const [k, v] of Object.entries(settings)) {
        if (k.endsWith('_api_key') && !editedKeys.has(k)) continue;
        payload[k] = typeof v === 'string' ? v : String(v ?? '');
      }
      await api.updateSettings(payload);
      setSaved(true);
      setEditedKeys(new Set());
      // Refresh so the newly-masked keys come back from the server.
      await loadData();
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  // ``provider`` is still read by the Active Configuration summary below
  // (kept as informational). Its setter (the old ``selectProvider`` that
  // auto-picked fast/capable models per provider) was removed along with
  // the three model cards — ModelConfigTable now handles provider selection
  // per use case.
  const provider = settings.llm_provider || 'openai';

  const setApiKey = (field: string, value: string) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setEditedKeys(prev => {
      const next = new Set(prev);
      next.add(field);
      return next;
    });
  };

  return (
    <div className="max-w-4xl mx-auto pt-8 pb-12 px-6">
      <div className="flex items-center gap-3 mb-6">
        <button data-testid="settings-back-button" onClick={onBack} className="text-zinc-500 hover:text-zinc-900 transition-colors">
          <ArrowLeft size={20} />
        </button>
        <h1 data-testid="settings-title" className="text-3xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          Settings
        </h1>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      <SystemConfigCollapsible />

      <div className="mb-6 mt-8 text-xs text-zinc-500 flex items-center gap-1.5">
        <Key size={12} /> LLM Configuration
      </div>

      {/* Active configuration summary — what the backend is actually using
          right now. Helps the drafter answer "which provider is the default?"
          without cross-referencing tiles, inputs, and hidden state. */}
      <div
        data-testid="active-config-summary"
        className="mb-6 border border-zinc-200 rounded-sm bg-zinc-50/50 p-4 text-sm"
      >
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Active configuration
        </div>
        <div className="space-y-1 text-zinc-700">
          {(() => {
            // Derive the effective provider from the model id. An
            // OpenRouter-prefixed value like "anthropic/claude-sonnet-4-6"
            // means the request is routed through OpenRouter regardless
            // of which radio button is lit — mirror the backend's
            // _resolve_config behaviour so the summary stops saying
            // "openai · anthropic/claude-sonnet-4-6" (obviously wrong).
            const effectiveProvider = (modelId?: string | null, base?: string): string => {
              const id = (modelId || '').trim();
              if (id && id.includes('/')) return 'openrouter';
              return (base || provider || '').trim();
            };
            const fastModel = activeModels?.fast_model || settings.fast_model || '';
            const capableModel = activeModels?.draft_model || settings.capable_model || '';
            const qcModel = activeModels?.qc_model || settings.qc_model || '';
            const fastProv = effectiveProvider(fastModel, settings.fast_provider);
            const capableProv = effectiveProvider(capableModel, settings.capable_provider);
            const qcProvRaw = settings.qc_provider
              || effectiveProvider(qcModel, settings.capable_provider);
            const qcProv = effectiveProvider(qcModel, qcProvRaw);
            return (
              <>
                <div>
                  <span className="text-zinc-500">Main provider: </span>
                  <span className="font-medium">{PROVIDERS.find(p => p.value === provider)?.label || provider}</span>
                  <span className="text-zinc-400 ml-1 text-xs">(default for all tiers below)</span>
                </div>
                <div>
                  <span className="text-zinc-500">Fast tier (scoring / quick tasks): </span>
                  <span className="font-mono">
                    {fastProv || provider} · {fastModel || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">Capable tier (drafting / analysis): </span>
                  <span className="font-mono">
                    {capableProv || provider} · {capableModel || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">QC reviewer (Stage 4): </span>
                  <span className="font-mono">
                    {(() => {
                      if (!qcModel && !settings.qc_provider) return 'Same as capable tier';
                      return `${qcProv || provider} · ${qcModel || capableModel || '—'}`;
                    })()}
                  </span>
                </div>
              </>
            );
          })()}
        </div>
      </div>

      <div className="space-y-6" data-testid="llm-settings">
          <div className="border border-zinc-200 rounded-sm bg-white p-6">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-sm font-medium text-zinc-900">API Keys</h3>
              {Object.keys(envSources).length > 0 && (
                <span
                  data-testid="env-sources-badge"
                  className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm"
                  title="These settings are currently being supplied by environment variables (e.g. Replit Secrets). They persist across redeploys regardless of what's in the database."
                >
                  {Object.keys(envSources).length} key{Object.keys(envSources).length === 1 ? '' : 's'} from env
                </span>
              )}
            </div>
            <p className="text-[11px] text-zinc-500 mb-4">
              Typed keys are stored in the local database. The database is gitignored but
              may not survive a redeploy — for production, set API keys as environment
              variables (e.g. <span className="font-mono">OPENAI_API_KEY</span>,{' '}
              <span className="font-mono">ANTHROPIC_API_KEY</span>) and they'll be picked
              up automatically.
            </p>
            <div className="space-y-4">
              {PROVIDERS.map(p => {
                const stored = settings[p.apiKeyField] || '';
                const edited = editedKeys.has(p.apiKeyField);
                const envVarName = envSources[p.apiKeyField];
                // Show a friendlier "key stored" placeholder instead of the
                // raw mask, and start the field empty so typing doesn't
                // append to the mask. If the drafter has typed, show what
                // they typed.
                const display = edited ? stored : '';
                const placeholder = !edited && stored
                  ? (envVarName ? `Sourced from ${envVarName} — type to override in DB` : 'Key stored — type to replace')
                  : p.placeholder;
                return (
                  <div key={p.apiKeyField}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <label className="text-xs text-zinc-500">{p.label} API Key</label>
                        {envVarName && (
                          <span
                            data-testid={`env-badge-${p.apiKeyField.replace(/_/g, '-')}`}
                            className="text-[9px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-1 py-0 rounded-sm"
                            title={`This key is being sourced from the ${envVarName} environment variable. Typing here stores an override in the DB; leave blank to keep using the env var.`}
                          >
                            env: {envVarName}
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-zinc-400 font-mono">{p.docs}</span>
                    </div>
                    <input
                      data-testid={`${p.apiKeyField.replace(/_/g, '-')}-input`}
                      type="password"
                      value={display}
                      onChange={(e) => setApiKey(p.apiKeyField, e.target.value)}
                      placeholder={placeholder}
                      className="w-full bg-white border border-zinc-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent font-mono"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* API key availability chips — one per provider. Purely
              informational; drafters use the API Keys card above to
              edit and ModelConfigTable below to route each use case
              to a provider. */}
          <ApiKeyStatusChips />

          {/* ModelConfigTable — the single surface for every
              model/provider pair. Replaces the previous LLM Provider,
              Model Selection, and QC Reviewer cards. Self-contained:
              fetches its own config, writes via PATCH
              /admin/config/{key} on save, probes via
              POST /admin/models/test on Test.

              onSaved pokes our own loadData so the ACTIVE CONFIGURATION
              panel above re-reads /settings/active-models + /settings
              after a row save. Without this the panel is loaded once
              on mount and happily displays the pre-edit model even
              though the pipeline is already calling the new one. */}
          <ModelConfigTable onSaved={loadData} />


          <div className="flex items-center gap-3">
            <button
              data-testid="save-settings-button"
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-[#002FA7] text-white hover:bg-[#002277] rounded-sm px-6 py-2.5 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : saved ? <CheckCircle size={16} /> : <Save size={16} />}
              {saved ? 'Saved!' : 'Save Settings'}
            </button>
            <button
              data-testid="llm-health-check-button"
              onClick={runHealthCheck}
              disabled={checking}
              className="bg-white text-zinc-900 border border-zinc-300 hover:bg-zinc-50 rounded-sm px-4 py-2.5 font-medium transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              {checking ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} />}
              Test LLM connection
            </button>
          </div>

          {health && (
            <div
              data-testid="llm-health-result"
              className={`mt-3 p-3 border rounded-sm text-sm ${
                health.status === 'ok'
                  ? 'bg-green-50 border-green-200 text-green-900'
                  : 'bg-red-50 border-red-200 text-red-900'
              }`}
            >
              <div className="font-medium mb-1">
                {health.status === 'ok' ? 'LLM reachable' : 'LLM unreachable'}
              </div>
              <div className="text-xs font-mono">
                provider={health.provider || '—'} · fast={health.model_fast || '—'} · capable={health.model_capable || '—'} · latency={health.latency_ms ?? '—'}ms
              </div>
              {health.error && <div className="text-xs mt-1 break-all">Error: {health.error}</div>}
            </div>
          )}

          <p className="text-xs text-zinc-400 pt-3 border-t border-zinc-100">
            Prompt templates are managed on the dedicated{' '}
            <span className="font-medium text-zinc-600">Prompts</span> page (top bar).
          </p>
      </div>
      <AppFooter />
    </div>
  );
}

// Thin wrapper that keeps the System Configuration panel collapsed
// by default (admin-heavy content; most drafters won't touch it).
// Renders the panel lazily so non-admin users don't fire a 403 on
// page load.
function SystemConfigCollapsible() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-4 border border-zinc-200 rounded-sm bg-white">
      <button
        type="button"
        data-testid="system-config-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2">
          <Sliders size={14} className="text-[#002FA7]" />
          <span className="text-sm font-medium text-zinc-900">System Configuration</span>
          <span className="text-[11px] text-zinc-400">admin · models, SMTP, PageIndex, session tuning</span>
        </div>
        {open ? <ChevronDown size={16} className="text-zinc-500" /> : <ChevronRight size={16} className="text-zinc-500" />}
      </button>
      {open && <SystemConfigPanel />}
    </div>
  );
}
